import { QuestionCircleOutlined } from '@ant-design/icons'
import { OrganizationContact } from '@prisma/client'
import { Avatar, Button, Flex, Tooltip, Typography } from 'antd'
import React from 'react'
import { OrganizationMemberFromContext } from '~/core/context/organization/hooks/useOrganizationMembers'
import { OrganizationMemberClientShared } from '~/features/organizationMember/clientShared'
import { Toolbox } from '~/plugins/toolbox/shared'
import { UIChatItem, UIChatReaction, UIChatThread } from '../../types'
import { FloatingToolbar } from '../FloatingToolbar'
import { RenderReactions } from '../RenderReactions'
import { useReactions } from '../RenderReactions/hooks/useReactions'
import { RenderTokens } from '../RenderTokens'
import { ThreadBar } from '../ThreadBar'
import { LoaderDots } from './../LoaderDots'
import { RenderAttachments } from './../RenderAttachments'
import { RenderMarkdownContent } from './../RenderMarkdownContent'
import { ContentInput } from './components/ContentInput'
import { Footer } from './components/Footer'
import { parseMessageContent } from './helpers'
import { useLogic } from './hooks/useLogic'

type Props = {
  item: UIChatItem
  organizationMember?: OrganizationMemberFromContext
  organizationContact?: OrganizationContact
  thread: UIChatThread
  onOpenThread?: () => void
  onReaction: (reaction: UIChatReaction) => void
  onSend?: (item: UIChatItem) => Promise<void>
  onDelete?: () => Promise<void>
  onAskForHelp: () => void
}

export const RenderMessage: React.FC<Props> = ({
  item: _item,
  organizationContact,
  organizationMember,
  thread,
  onOpenThread,
  onReaction,
  onSend,
  onDelete,
  onAskForHelp = () => {},
}) => {
  const logic = useLogic({ item: _item, onSend })

  const item = logic.item

  const { content, tokens } = parseMessageContent(item)

  const logoUrl = item?.values?.logoUrl
  const attachments = item?.values?.attachments ?? []

  const isDraft = item?.status === 'DRAFT' && !!onSend
  const isTyping = item?.values?.isTyping
  const isError = item?.values?.isError
  const contentTyping = item?.values?.typingContent
  const isMessage = Toolbox.Utility.isDefined(content)
  const isMessageTyping = Toolbox.Utility.isDefined(contentTyping)
  const contentError = item?.values?.errorContent ?? 'Something went wrong'

  const nameFromContact = [
    organizationContact?.firstname,
    organizationContact?.lastname,
    organizationContact?.email,
  ].filter(Boolean)[0]
  const name =
    nameFromContact ??
    OrganizationMemberClientShared.Helper.getName(organizationMember)
  const date = Toolbox.Date.toAsanaStyle(item.createdAt)
  const time = Toolbox.Date.toSlackTimeStyle(item.createdAt)
  const dateFull = `${date} at ${time}`
  const canStartThread = !item.parentId && onOpenThread
  const canViewThread = thread.countMessages > 0

  const { reactions, handleReaction } = useReactions({ item, onReaction })

  return (
    <>
      <FloatingToolbar
        item={item}
        onReaction={handleReaction}
        onOpenThread={canStartThread ? onOpenThread : null}
      />

      <Flex vertical gap={8} className={`w-full`}>
        <Flex gap={8} align="center">
          <Typography.Text strong style={{ fontSize: '15px' }}>
            {name}
          </Typography.Text>

          <Tooltip title={dateFull} placement="top" mouseEnterDelay={0.5}>
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              {time}
            </Typography.Text>
          </Tooltip>
        </Flex>

        {logoUrl && <Avatar src={logoUrl} size={20} shape="square" />}

        {isDraft && (
          <Typography.Text underline type="danger">
            Draft
          </Typography.Text>
        )}

        {isError ? (
          <>
            <Typography.Text type="danger">{contentError}</Typography.Text>
            <div>
              <Button onClick={onAskForHelp}>
                <QuestionCircleOutlined /> Ask for help
              </Button>
            </div>
          </>
        ) : isTyping ? (
          <>
            <Flex gap={8}>
              {isMessageTyping && (
                <RenderMarkdownContent
                  type="secondary"
                  italic
                  content={contentTyping}
                />
              )}
              <Flex style={{ paddingTop: '9px' }}>
                <LoaderDots />
              </Flex>
            </Flex>
          </>
        ) : isMessage ? (
          <>
            {isDraft ? (
              <ContentInput item={item} onChange={logic.onChangeContent} />
            ) : (
              <RenderMarkdownContent content={content} />
            )}
          </>
        ) : (
          <></>
        )}

        {tokens.length > 0 && (
          <RenderTokens
            tokens={tokens}
            organizationMember={organizationMember}
          />
        )}

        {attachments.length > 0 && (
          <RenderAttachments attachments={attachments} />
        )}

        <Footer item={item} onSend={logic.send} onDelete={onDelete} />

        {/* {posts.length > 0 && <RenderPosts posts={posts} />} */}

        <RenderReactions reactions={reactions} />

        {canViewThread && onOpenThread && (
          <ThreadBar thread={thread} onOpenThread={onOpenThread} />
        )}
      </Flex>
    </>
  )
}
